import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { clearOps } from "@/lib/offlineQueue";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({ user: null, capabilities: null, loading: true });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setState({ user: data.user, capabilities: data.capabilities, loading: false });
      return data.user;
    } catch {
      setState({ user: null, capabilities: null, loading: false });
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(
    async (email, password, rememberMe = false) => {
      try {
        await api.post("/auth/login", { email, password, remember_me: rememberMe });
        if (rememberMe) localStorage.setItem("okn-remember-email", email);
        else localStorage.removeItem("okn-remember-email");
        const user = await refresh();
        return { ok: true, user };
      } catch (error) {
        return { ok: false, error: apiError(error) };
      }
    },
    [refresh]
  );

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      return { ok: true, message: data.message };
    } catch (error) {
      return { ok: false, error: apiError(error) };
    }
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    await clearOps().catch(() => {});
    localStorage.removeItem("okn-panel-width");
    setState({ user: null, capabilities: null, loading: false });
  }, []);

  const value = useMemo(
    () => ({ ...state, login, register, logout, refresh }),
    [state, login, register, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function canEdit(capabilities, department) {
  if (!capabilities) return false;
  return (capabilities.editable_departments || []).includes(department);
}

export function canEditData(capabilities, key, user) {
  if (!capabilities || !user) return false;
  if (capabilities.is_admin) return true;
  if (capabilities.is_guest) return false;
  const allowed = capabilities.data_editors?.[key] || [];
  return (user.roles || []).some((r) => allowed.includes(r));
}

/** Guest is a read-only viewer: every mutation surface must be hidden. */
export function isGuest(capabilities) {
  return Boolean(capabilities?.is_guest);
}
